import { useState, useEffect, useRef } from 'react';

// 이미지 캐시 관리 유틸리티
class ImageCacheManager {
  private cache: Map<string, string> = new Map();
  private loadingPromises: Map<string, Promise<string>> = new Map();
  private loadTimes: Map<string, number> = new Map();
  private loadedImages: Set<string> = new Set();
  private failedImages: Set<string> = new Set();

  // 이미지 URL 생성 (버전 포함)
  buildImageUrl(path: string, version?: number): string {
    // path가 백슬래시를 포함하고 있으면 슬래시로 변경
    const normalizedPath = path.replace(/\\/g, '/');
    // 콜라보 짝 표(`?c=`)가 이미 붙어 있을 수 있다 — 그때는 `&` 로 잇는다
    const versionParam = version ? `${normalizedPath.includes('?') ? '&' : '?'}v=${version}` : '';
    return `/mobs/v2/${normalizedPath}${versionParam}`;
  }

  // 이미지가 로딩되었는지 확인
  isImageLoaded(url: string): boolean {
    return this.loadedImages.has(url);
  }

  // 이미지가 실패했는지 확인
  isImageFailed(url: string): boolean {
    return this.failedImages.has(url);
  }

  // 이미지 로딩 성공 표시
  markImageLoaded(url: string): void {
    this.loadedImages.add(url);
    this.failedImages.delete(url);
  }

  // 이미지 로딩 실패 표시
  markImageFailed(url: string): void {
    this.failedImages.add(url);
    this.loadedImages.delete(url);
  }

  async getImageUrl(path: string, version?: number): Promise<string> {
    const url = this.buildImageUrl(path, version);
    const cacheKey = `${path}:${version || 'default'}`;

    // 캐시된 URL이 있으면 반환
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // 이미 로딩 중이면 해당 Promise 반환
    if (this.loadingPromises.has(cacheKey)) {
      return this.loadingPromises.get(cacheKey)!;
    }

    // 새로운 이미지 로딩
    const loadingPromise = this.loadImage(url).then(loadedUrl => {
      this.cache.set(cacheKey, loadedUrl);
      this.loadTimes.set(cacheKey, Date.now());
      this.loadingPromises.delete(cacheKey);
      return loadedUrl;
    }).catch(error => {
      this.loadingPromises.delete(cacheKey);
      throw error;
    });

    this.loadingPromises.set(cacheKey, loadingPromise);
    return loadingPromise;
  }

  private loadImage(url: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        this.markImageLoaded(url);
        resolve(url);
      };
      img.onerror = () => {
        this.markImageFailed(url);
        resolve('/images/empty.png'); // fallback
      };
      img.src = url;
    });
  }

  getCachedImageUrl(path: string, version?: number): string | null {
    const cacheKey = `${path}:${version || 'default'}`;
    return this.cache.get(cacheKey) || null;
  }

  invalidateImage(path: string, version?: number): void {
    const cacheKey = `${path}:${version || 'default'}`;
    const url = this.buildImageUrl(path, version);

    this.cache.delete(cacheKey);
    this.loadTimes.delete(cacheKey);
    this.loadingPromises.delete(cacheKey);
    this.loadedImages.delete(url);
    this.failedImages.delete(url);
  }

  // 전체 캐시 무효화
  clearCache(): void {
    this.cache.clear();
    this.loadTimes.clear();
    this.loadingPromises.clear();
    this.loadedImages.clear();
    this.failedImages.clear();
  }

  // 캐시 크기 가져오기
  getCacheSize(): number {
    return this.loadedImages.size;
  }

  // 오래된 캐시 정리 (1시간 이상된 것들)
  cleanupOldCache(maxAge: number = 60 * 60 * 1000): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.loadTimes.forEach((loadTime, cacheKey) => {
      if (now - loadTime > maxAge) {
        keysToDelete.push(cacheKey);
      }
    });

    keysToDelete.forEach(cacheKey => {
      this.cache.delete(cacheKey);
      this.loadTimes.delete(cacheKey);
    });
  }

  async preloadImages(paths: string[], version?: number): Promise<void> {
    const promises = paths.map(path =>
      this.getImageUrl(path, version).catch(error => {
        return null;
      })
    );

    await Promise.all(promises);
  }
}

// 전역 이미지 캐시 인스턴스
export const imageCache = new ImageCacheManager();

export function useImageCache(path: string, version?: number, fallbackUrl: string = '/images/empty.png') {
  // URL을 즉시 계산하고 상태로 유지
  const imageUrl = !path ? fallbackUrl : imageCache.buildImageUrl(path, version);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  
  // 이전 URL 추적
  const prevUrlRef = useRef<string>('');
  
  useEffect(() => {
    // URL이 변경되지 않았으면 아무것도 하지 않음
    if (prevUrlRef.current === imageUrl) {
      return;
    }

    prevUrlRef.current = imageUrl;

    // fallback URL이면 처리하지 않음
    if (imageUrl === fallbackUrl) {
      setIsLoading(false);
      setError(false);
      return;
    }

    // 이미 로딩된 이미지면 바로 완료
    if (imageCache.isImageLoaded(imageUrl)) {
      setIsLoading(false);
      setError(false);
      return;
    }

    // 이미 실패한 이미지면 에러 처리
    if (imageCache.isImageFailed(imageUrl)) {
      setIsLoading(false);
      setError(true);
      return;
    }

    // 새 이미지 로딩 시작
    setIsLoading(true);
    setError(false);

    const img = new Image();
    img.onload = () => {
      imageCache.markImageLoaded(imageUrl);
      setIsLoading(false);
      setError(false);
    };
    img.onerror = () => {
      imageCache.markImageFailed(imageUrl);
      setIsLoading(false);
      setError(true);
    };
    img.src = imageUrl;

    // cleanup
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl, fallbackUrl]); // imageUrl이 변경될 때만 실행
  
  return { imageUrl, isLoading, error };
}

// 몬스터 이미지 전용 Hook
export function useMonsterImage(imgPath: string | null, version?: number) {
  return useImageCache(
    imgPath || '', 
    version, 
    '/images/empty.png'
  );
} 